import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, CompFlashClient, browserTokenStore } from '@compflash/client';
import type { AuthUser, Entitlement, Usage } from '@compflash/contracts';

const API_BASE_URL = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? 'http://localhost:8787';

interface SessionValue {
  client: CompFlashClient;
  user: AuthUser | null;
  entitlement: Entitlement | null;
  usage: Usage | null;
  loading: boolean;
  signIn(email: string, password: string): Promise<void>;
  register(email: string, password: string, displayName?: string): Promise<void>;
  signOut(): Promise<void>;
  refreshAccount(): Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState(true);
  const [signedOutAt, setSignedOutAt] = useState(0);

  const client = useMemo(
    () =>
      new CompFlashClient({
        baseUrl: API_BASE_URL,
        tokens: browserTokenStore(),
        onSignedOut: () => setSignedOutAt(Date.now()),
      }),
    [],
  );

  useEffect(() => {
    if (signedOutAt === 0) return;
    setUser(null);
    setEntitlement(null);
    setUsage(null);
  }, [signedOutAt]);

  const refreshAccount = useCallback(async () => {
    // No stored session means no point asking the server; this keeps anonymous
    // page loads free of guaranteed-401 requests.
    if (!client.hasSession()) {
      setUser(null);
      setEntitlement(null);
      setUsage(null);
      return;
    }
    try {
      const [me, billing] = await Promise.all([client.me(), client.entitlements()]);
      setUser(me.user);
      setEntitlement(billing.entitlement);
      setUsage(billing.usage);
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized) {
        setUser(null);
        setEntitlement(null);
        setUsage(null);
        return;
      }
      throw error;
    }
  }, [client]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await refreshAccount();
      } catch {
        // The API may be unreachable; sign-in still renders.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshAccount]);

  const value: SessionValue = {
    client,
    user,
    entitlement,
    usage,
    loading,
    async signIn(email, password) {
      await client.login(email, password);
      await refreshAccount();
    },
    async register(email, password, displayName) {
      await client.register(email, password, displayName);
      await refreshAccount();
    },
    async signOut() {
      await client.logout();
      setUser(null);
      setEntitlement(null);
      setUsage(null);
    },
    refreshAccount,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a SessionProvider');
  return value;
}
